import { lazy, Suspense, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { auth, getEventDocumentState, signOutCurrentUser } from "./firebase";
import Invitation from "./Pages/Invitation";
import Login from "./Pages/Login";
import Manager from "./Pages/Manager";
import Thanks from "./Pages/Thanks";
import Welcome from "./Pages/Welcome";

const Manual = lazy(() => import("./Pages/Manual"));
const AdminContactImport = lazy(() => import("./Pages/AdminContactImport"));

const AuthLoading = () => (
  <main
    className="col-12 d-flex justify-content-center align-items-center"
    dir="rtl"
    style={{ minHeight: "100vh", padding: "24px", background: "#f4f8f7", color: "#173b3a" }}
  >
    <div role="status" aria-live="polite" style={{ fontSize: "1.05rem", fontWeight: 700 }}>
      בודקים את החיבור למערכת...
    </div>
  </main>
);

const AppRouter = ({ addAlertText }) => {
  const [docID, setDocID] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [missingEvent, setMissingEvent] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          try {
            const event = await getEventDocumentState(user.uid);
            // Login now continues in the same page; ignore a check for an account that
            // was replaced while it ran (e.g. opening an access link in a signed-in browser).
            if (auth.currentUser?.uid !== user.uid) return;
            if (event.exists) {
              setDocID(user.uid);
              setMissingEvent(false);
              window.sessionStorage.setItem("login", JSON.stringify({ id: user.uid, isAuth: true }));
            } else {
              setDocID("");
              setMissingEvent(true);
              window.sessionStorage.removeItem("login");
            }
          } catch (_) {
            if (auth.currentUser?.uid !== user.uid) return;
            setDocID("");
            setMissingEvent(true);
          }
        } else {
          setDocID("");
          setMissingEvent(false);
          window.sessionStorage.removeItem("login");
        }
        setAuthReady(true);
      },
      () => {
        setDocID("");
        setAuthReady(true);
      }
    );

    return unsubscribe;
  }, []);
  return (
    <Router>
      <Routes>
        <Route exact path="/" element={<Login addAlertText={addAlertText} docID={docID} setDocID={setDocID} />} />
        <Route exact path="/signup" element={<Login addAlertText={addAlertText} docID={docID} setDocID={setDocID} initialSignupMode />} />
        <Route exact path="/welcome" element={<Welcome />} />
        <Route
          exact
          path="/manual"
          element={
            <Suspense fallback={<div className="p-4 text-center">טוען מדריך...</div>}>
              <Manual />
            </Suspense>
          }
        />
        <Route
          exact
          path="/manage"
          element={
            !authReady ? (
              <AuthLoading />
            ) : docID ? (
              <Manager addAlertText={addAlertText} docID={docID} />
            ) : missingEvent ? (
              <main className="p-4 text-center" dir="rtl" role="status">
                <h1>המערכת עדיין לא מוכנה</h1>
                <p>ההתחברות הצליחה, אבל לא נמצא אירוע מוכן. אפשר לנסות שוב או לצאת ולפנות לתמיכה.</p>
                <button type="button" className="btn btn-outline-secondary" onClick={() => window.location.reload()}>בדיקה חוזרת</button>{" "}
                <button type="button" className="btn btn-link" onClick={() => signOutCurrentUser().then(() => { window.location.href = "/"; })}>יציאה</button>
              </main>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          exact
          path="/internal/whatsapp-import"
          element={
            <Suspense fallback={<AuthLoading />}>
              <AdminContactImport />
            </Suspense>
          }
        />
        <Route exact path="/invitation/:id/:phone" element={<Invitation addAlertText={addAlertText} />} />
        <Route exact path="/invitation/:id" element={<Invitation addAlertText={addAlertText} />} />
        <Route path="/thanks" element={<Thanks />} />
        <Route path="*" element={<div>404</div>} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
